import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { CheckCircle2, XCircle, RotateCcw, Shuffle, GripVertical } from 'lucide-react';

// A term→definition matcher with smooth pointer-based drag & drop, snap-to-target,
// live correct/incorrect validation, shuffle and reset. No external deps — uses
// Pointer Events so it works with mouse, touch and pen. Also fully keyboard
// operable (pick up a chip with Enter/Space, choose a slot, drop with Enter).
//
// Public props unchanged: { pairs, title?, onComplete }.

export interface MatchPair {
  term: string;
  match: string;
}

interface Props {
  pairs: MatchPair[];
  title?: string;
  onComplete?: () => void;
}

interface DragState {
  match: string; // the chip being dragged
  pointerId: number;
  // pointer offset within the chip, so it tracks under the cursor naturally
  offsetX: number;
  offsetY: number;
  x: number; // current viewport coords of the chip's top-left
  y: number;
  w: number;
  h: number;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function DragDropMatcher({ pairs, title, onComplete }: Props) {
  // termIndex -> the match string currently placed on it
  const [placed, setPlaced] = useState<Record<number, string>>({});
  const [order, setOrder] = useState<string[]>(() => shuffle(pairs.map((p) => p.match)));
  const [drag, setDrag] = useState<DragState | null>(null);
  const [hoverTerm, setHoverTerm] = useState<number | null>(null);
  // keyboard "carry" mode: a chip picked up via keyboard, awaiting a target
  const [carry, setCarry] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  const completedRef = useRef(false);
  const slotRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Re-seed when the pairs prop changes identity.
  useEffect(() => {
    setOrder(shuffle(pairs.map((p) => p.match)));
    setPlaced({});
    setDrag(null);
    setCarry(null);
    setChecked(false);
    completedRef.current = false;
  }, [pairs]);

  const usedMatches = useMemo(() => new Set(Object.values(placed)), [placed]);
  const allFilled = pairs.length > 0 && Object.keys(placed).length === pairs.length;
  const allCorrect = pairs.length > 0 && pairs.every((p, i) => placed[i] === p.match);

  const placeMatch = useCallback(
    (termIdx: number, match: string) => {
      setPlaced((prev) => {
        const next: Record<number, string> = {};
        for (const [k, v] of Object.entries(prev)) {
          if (v !== match) next[Number(k)] = v; // a chip lives in one slot only
        }
        next[termIdx] = match;
        const correct = pairs.length > 0 && pairs.every((p, i) => next[i] === p.match);
        if (correct && !completedRef.current) {
          completedRef.current = true;
          queueMicrotask(() => onComplete?.());
        }
        return next;
      });
    },
    [pairs, onComplete],
  );

  // Which term slot (if any) is under a viewport point — used for snap targeting.
  const slotAtPoint = useCallback((px: number, py: number): number | null => {
    for (const [k, el] of Object.entries(slotRefs.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (px >= r.left && px <= r.right && py >= r.top && py <= r.bottom) return Number(k);
    }
    return null;
  }, []);

  // ── Pointer drag lifecycle ────────────────────────────────────────────────
  const onChipPointerDown = useCallback(
    (e: React.PointerEvent, match: string) => {
      // A placed chip can be re-dragged back out — no guard needed.
      const el = e.currentTarget as HTMLElement;
      const r = el.getBoundingClientRect();
      el.releasePointerCapture?.(e.pointerId);
      setCarry(null);
      setDrag({
        match,
        pointerId: e.pointerId,
        offsetX: e.clientX - r.left,
        offsetY: e.clientY - r.top,
        x: r.left,
        y: r.top,
        w: r.width,
        h: r.height,
      });
    },
    [],
  );

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== drag.pointerId) return;
      const x = e.clientX - drag.offsetX;
      const y = e.clientY - drag.offsetY;
      setDrag((d) => (d ? { ...d, x, y } : d));
      setHoverTerm(slotAtPoint(e.clientX, e.clientY));
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== drag.pointerId) return;
      const target = slotAtPoint(e.clientX, e.clientY);
      if (target !== null) placeMatch(target, drag.match);
      setDrag(null);
      setHoverTerm(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [drag, slotAtPoint, placeMatch]);

  // ── Keyboard carry mode ───────────────────────────────────────────────────
  const toggleCarry = useCallback((match: string) => {
    setCarry((c) => (c === match ? null : match));
  }, []);

  const onSlotKeyDown = useCallback(
    (e: React.KeyboardEvent, termIdx: number) => {
      if ((e.key === 'Enter' || e.key === ' ') && carry) {
        e.preventDefault();
        placeMatch(termIdx, carry);
        setCarry(null);
      } else if ((e.key === 'Backspace' || e.key === 'Delete') && placed[termIdx]) {
        e.preventDefault();
        setPlaced((prev) => {
          const next = { ...prev };
          delete next[termIdx];
          return next;
        });
      }
    },
    [carry, placed, placeMatch],
  );

  const reset = useCallback(() => {
    setPlaced({});
    setCarry(null);
    setChecked(false);
    completedRef.current = false;
  }, []);

  const reshuffle = useCallback(() => {
    setOrder(shuffle(pairs.map((p) => p.match)));
    reset();
  }, [pairs, reset]);

  const correctCount = pairs.filter((p, i) => placed[i] === p.match).length;
  // Show per-slot validation colours only after an explicit check, OR live once
  // the board is full (so the learner gets immediate feedback at the end).
  const showValidation = checked || allFilled;

  if (pairs.length === 0) {
    return (
      <div className="rounded-md border border-ws-border bg-ws-surface p-6 text-sm text-ws-ink-3">
        No items to match.
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-2xl flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="m-0 text-base font-semibold text-ws-ink">{title ?? 'Match the pairs'}</h3>
        <div className="flex items-center gap-2">
          {allCorrect ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-ws-success">
              <CheckCircle2 size={14} /> All matched
            </span>
          ) : (
            <span className="text-xs text-ws-ink-3">
              {correctCount}/{pairs.length} correct
            </span>
          )}
          <button
            type="button"
            onClick={reshuffle}
            className="flex items-center gap-1 rounded-sm px-2.5 py-1 text-xs text-ws-ink-2 transition-colors hover:bg-ws-surface-2"
          >
            <Shuffle size={12} /> Shuffle
          </button>
          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1 rounded-sm px-2.5 py-1 text-xs text-ws-ink-2 transition-colors hover:bg-ws-surface-2"
          >
            <RotateCcw size={12} /> Reset
          </button>
        </div>
      </div>

      {carry && (
        <p className="rounded-sm border-l-2 border-ws-accent bg-ws-accent-quiet px-3 py-1.5 text-xs text-ws-accent-ink">
          Carrying <strong>{carry}</strong> — focus a slot and press Enter to drop, or press its
          chip again to cancel.
        </p>
      )}

      <div className="grid grid-cols-[1fr_minmax(0,14rem)] gap-4">
        {/* Term slots (drop targets) */}
        <div className="flex flex-col gap-2">
          {pairs.map((p, i) => {
            const value = placed[i];
            const correct = value === p.match;
            const isHover = hoverTerm === i;
            let slotCls = 'border-ws-border text-ws-ink-4';
            if (value) {
              if (showValidation) {
                slotCls = correct
                  ? 'border-ws-success bg-ws-success/10 text-ws-ink'
                  : 'border-ws-danger bg-ws-danger/10 text-ws-ink';
              } else {
                slotCls = 'border-ws-accent bg-ws-accent-quiet text-ws-ink';
              }
            } else if (isHover) {
              slotCls = 'border-ws-accent bg-ws-accent-quiet text-ws-ink';
            }
            return (
              <div
                key={i}
                ref={(el) => {
                  slotRefs.current[i] = el;
                }}
                className={`flex items-center gap-3 rounded-sm border bg-ws-tile p-3 transition-colors ${
                  isHover ? 'border-ws-accent' : 'border-ws-border'
                }`}
              >
                <span className="flex-1 text-sm font-medium leading-snug text-ws-ink">{p.term}</span>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={
                    value
                      ? `${p.term} matched with ${value}. ${
                          showValidation ? (correct ? 'Correct.' : 'Incorrect.') : ''
                        } Press Backspace to clear.`
                      : `Empty slot for ${p.term}.${carry ? ' Press Enter to drop carried chip.' : ''}`
                  }
                  onKeyDown={(e) => onSlotKeyDown(e, i)}
                  className={`flex min-h-9 min-w-32 items-center justify-center rounded-sm border-2 border-dashed px-3 py-1.5 text-center text-sm outline-none transition-all focus-visible:ring-2 focus-visible:ring-ws-accent ${slotCls}`}
                >
                  <span className="flex items-center gap-1.5">
                    {value && showValidation && (
                      correct ? (
                        <CheckCircle2 size={13} className="text-ws-success" />
                      ) : (
                        <XCircle size={13} className="text-ws-danger" />
                      )
                    )}
                    {value ?? 'Drop here'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Draggable answer chips */}
        <div className="flex flex-col gap-2">
          {order.map((chip) => {
            const used = usedMatches.has(chip);
            const isDragging = drag?.match === chip;
            const isCarried = carry === chip;
            return (
              <div
                key={chip}
                role="button"
                tabIndex={0}
                aria-grabbed={isDragging || isCarried}
                aria-label={`${chip}. Press Enter to pick up, then choose a slot.`}
                onPointerDown={(e) => onChipPointerDown(e, chip)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCarry(chip);
                  }
                }}
                className={`flex touch-none items-center gap-1.5 rounded-sm border px-3 py-1.5 text-sm outline-none transition-all focus-visible:ring-2 focus-visible:ring-ws-accent ${
                  isDragging
                    ? 'opacity-30'
                    : isCarried
                      ? 'border-ws-accent bg-ws-accent ring-2 ring-ws-accent text-white'
                      : used
                        ? 'cursor-grab border-ws-border bg-ws-surface-2 text-ws-ink-3'
                        : 'cursor-grab border-ws-accent bg-ws-accent-quiet text-ws-accent-ink active:cursor-grabbing'
                }`}
              >
                <GripVertical size={13} className="flex-shrink-0 opacity-50" />
                <span className="truncate">{chip}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {!allCorrect && (
          <button
            type="button"
            onClick={() => setChecked(true)}
            disabled={Object.keys(placed).length === 0}
            className="rounded-sm bg-ws-accent px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-ws-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Check answers
          </button>
        )}
      </div>

      {/* Floating drag ghost — follows the pointer, snaps visually via hover state. */}
      {drag && (
        <div
          className="pointer-events-none fixed z-50 flex items-center gap-1.5 rounded-sm border border-ws-accent bg-ws-accent px-3 py-1.5 text-sm text-white shadow-lg"
          style={{ left: drag.x, top: drag.y, width: drag.w, minHeight: drag.h }}
        >
          <GripVertical size={13} className="flex-shrink-0 opacity-70" />
          <span className="truncate">{drag.match}</span>
        </div>
      )}
    </div>
  );
}
